import { memo, useRef, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spin, Skeleton } from 'antd';
import Row from 'antd/lib/row';
import { useDispatch, useSelector } from 'react-redux';
import {
  firstGetListMessage,
  retryTempMessage,
  removeTempMessage,
  sendMessageRequest,
  markTempMessageFailed,
  finishSendMessage,
} from 'providers/MessengerProvider/slice';
import MessageCell from './MessageCell';
import { ArrowDownOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import moment from 'moment';
import classNames from 'classnames';

const formatDateHeader = (date) => {
  if (!date) return '';
  // Explicitly use local timezone for consistent date display
  const messageDate = moment(date).local();
  const today = moment().local().startOf('day');
  const currentYear = moment().year();

  if (messageDate.isSame(today, 'day')) {
    return 'Hôm nay';
  }

  const day = messageDate.format('DD');
  const month = messageDate.format('MM');
  const year = messageDate.year();

  if (year === currentYear) {
    return `${day} tháng ${month}`;
  }

  return `${day} tháng ${month}, ${year}`;
};

const getDateKey = (date) => {
  if (!date) return '';
  // Use local timezone and format as YYYY-MM-DD for consistent date grouping
  // Using format() instead of valueOf() ensures the same local date produces the same key
  return moment(date).local().format('YYYY-MM-DD');
};

// Skeleton for loading older messages
const MessageSkeleton = ({ isOwn = false }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: isOwn ? 'flex-end' : 'flex-start',
      padding: '8px 16px',
      gap: 8,
    }}
  >
    {!isOwn && <Skeleton.Avatar active size={32} />}
    <div style={{ maxWidth: '60%' }}>
      <Skeleton.Input active size="small" style={{ width: isOwn ? 150 : 200, marginBottom: 4 }} />
      <Skeleton.Input active size="small" style={{ width: isOwn ? 100 : 120 }} />
    </div>
    {isOwn && <Skeleton.Avatar active size={32} />}
  </div>
);

const MessageList = ({
  messageIds = [],
  messageEntities = {},
  getMessageList,
  hasMore,
  currentUserId,
  currentUserRole,
}) => {
  const dispatch = useDispatch();
  const latestMessage = messageIds[0];
  const messageListRef = useRef()
  const firstGetList = useSelector((state) => state.messenger.firstGetList);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [newMessagePreview, setNewMessagePreview] = useState(null);
  const prevLatestMessageRef = useRef(latestMessage);
  const isViewingOldMessagesRef = useRef(false);
  const [stickyDate, setStickyDate] = useState(null);
  const [stickyDateVisible, setStickyDateVisible] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const stickyDateRef = useRef(null);

  useLayoutEffect(() => {
    if (messageListRef.current) {
      dispatch(firstGetListMessage());

      const isNewMessage = prevLatestMessageRef.current && prevLatestMessageRef.current !== latestMessage;

      if (isNewMessage && isViewingOldMessagesRef.current) {
        // User is viewing old messages - show notification instead of scrolling
        const newMessage = messageEntities[latestMessage];
        if (newMessage) {
          const senderId = _get(newMessage, 'sender._id') || _get(newMessage, 'sender');
          const isOwnMessage = senderId === currentUserId;

          if (!isOwnMessage) {
            // Show preview for messages from others
            const senderName = _get(newMessage, 'sender.name') || _get(newMessage, 'sender.username') || 'Fonehouse';
            const messageContent = _get(newMessage, 'text') || _get(newMessage, 'content') || 'Tin nhắn mới';

            setNewMessagePreview({
              senderName,
              content: messageContent,
            });
          } else {
            // Own message - scroll to bottom
            messageListRef.current.scrollTo(0, 0);
            setNewMessagePreview(null);
          }
        }
      } else if (!isViewingOldMessagesRef.current) {
        // Not viewing old messages - auto scroll to bottom
        messageListRef.current.scrollTo(0, 0);
        setNewMessagePreview(null);
      }

      prevLatestMessageRef.current = latestMessage;
    }
  }, [dispatch, latestMessage, messageEntities, currentUserId]);

  // Attach wheel event listener to capture scroll immediately
  useEffect(() => {
    const container = messageListRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      // If we can scroll in the wheel direction, prevent parent from scrolling
      const canScrollUp = !isAtTop && scrollingUp;
      const canScrollDown = !isAtBottom && scrollingDown;

      if (canScrollUp || canScrollDown) {
        e.stopPropagation();
      }
    };

    // Use capture phase to intercept event before it bubbles
    container.addEventListener('wheel', handleWheel, { passive: true, capture: true });

    return () => {
      container.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, []);

  // Handle scroll to show/hide scroll down button and sticky date header
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = Math.abs(scrollTop) < 100;
    const hasScrollableContent = scrollHeight > clientHeight;
    const isViewingOld = hasScrollableContent && !isAtBottom;

    isViewingOldMessagesRef.current = isViewingOld;
    setShowScrollDown(isViewingOld);

    if (!isViewingOld) {
      setNewMessagePreview(null);
      setStickyDateVisible(false);
      return;
    }

    const dateHeaders = Array.from(container.querySelectorAll('[data-date-header]'));
    const containerRect = container.getBoundingClientRect();
    const stickyHeaderElement = stickyDateRef.current;
    const stickyHeaderHeight = stickyHeaderElement ? stickyHeaderElement.offsetHeight : 24;
    const stickyHeaderTop = 12;
    const stickyBottom = stickyHeaderTop + stickyHeaderHeight;
    const detectionLine = stickyBottom + 10;

    const dateHeadersWithPosition = [];
    for (const header of dateHeaders) {
      const rect = header.getBoundingClientRect();
      const relativeTop = rect.top - containerRect.top;
      dateHeadersWithPosition.push({
        element: header,
        date: header.getAttribute('data-date-header'),
        top: relativeTop,
      });
    }

    dateHeadersWithPosition.sort((a, b) => a.top - b.top);

    let currentDate = null;
    let currentDateHeaderTop = null;

    for (let i = 0; i < dateHeadersWithPosition.length; i++) {
      const header = dateHeadersWithPosition[i];
      if (header.top <= detectionLine) {
        currentDate = header.date;
        currentDateHeaderTop = header.top;
      } else {
        if (currentDate === null) {
          currentDate = header.date;
          currentDateHeaderTop = header.top;
        }
        break;
      }
    }

    if (!currentDate && dateHeadersWithPosition.length > 0) {
      const firstHeader = dateHeadersWithPosition[0];
      currentDate = firstHeader.date;
      currentDateHeaderTop = firstHeader.top;
    }

    if (!currentDate) {
      setStickyDateVisible(false);
      return;
    }

    let merging = false;
    let mergeOffset = 0;
    const mergeStartDistance = 40;
    const mergeEndDistance = 50;

    const currentHeaderBelowSticky = currentDateHeaderTop !== null && currentDateHeaderTop > stickyBottom;
    const currentHeaderNearSticky = currentDateHeaderTop !== null && currentDateHeaderTop > 0 && currentDateHeaderTop <= stickyBottom + mergeStartDistance;

    if (currentHeaderBelowSticky && currentHeaderNearSticky) {
      const distanceToHeader = currentDateHeaderTop - stickyBottom;
      if (distanceToHeader <= mergeStartDistance && distanceToHeader > 0) {
        merging = true;
        mergeOffset = mergeStartDistance - distanceToHeader;
      }
    }

    setIsMerging(merging);

    if (stickyHeaderElement) {
      if (merging) {
        const progress = Math.min(mergeOffset / mergeEndDistance, 1);
        stickyHeaderElement.style.transform = `translateX(-50%) translateY(${mergeOffset}px)`;
        stickyHeaderElement.style.opacity = Math.max(0, 1 - progress);
      } else {
        stickyHeaderElement.style.transform = 'translateX(-50%) translateY(0)';
        stickyHeaderElement.style.opacity = '';
      }
    }

    const currentHeaderPassedSticky = currentDateHeaderTop !== null && currentDateHeaderTop <= stickyBottom;
    const shouldShowSticky = currentHeaderPassedSticky || currentHeaderBelowSticky;

    if (shouldShowSticky) {
      setStickyDate(currentDate);
      setStickyDateVisible(true);
    } else {
      setStickyDateVisible(false);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTo(0, 0);
      isViewingOldMessagesRef.current = false;
      setNewMessagePreview(null);
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Chỉ focus nếu không có input/textarea nào đang được focus
    const activeElement = document.activeElement;
    const isInputFocused = activeElement &&
      (activeElement.tagName === 'INPUT' ||
       activeElement.tagName === 'TEXTAREA' ||
       activeElement.isContentEditable);

    if (messageListRef.current && !isInputFocused) {
      messageListRef.current.focus();
    }
  }, []);

  // Handle retry for failed messages
  const handleRetry = useCallback(
    (failedMessage) => {
      const tempId = failedMessage._id || failedMessage.tempId;
      if (!tempId) return;

      // Mark as sending again via Redux
      dispatch(retryTempMessage({ tempId }));

      // Get original payload
      const payload = failedMessage._originalPayload;
      if (!payload) return;

      const trimmedMessage = payload.message?.trim();

      // Dispatch API call with timeout (10s)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Gửi tin nhắn quá lâu. Vui lòng thử lại.')), 10000);
      });

      Promise.race([
        dispatch(sendMessageRequest({
          type: 'text',
          text: trimmedMessage || null,
          conversationId: failedMessage.conversationId,
          attachments: payload.attachments || [],
        })),
        timeoutPromise
      ]).then(() => {
        // Success - message sẽ được confirm qua socket
        dispatch(finishSendMessage());
      }).catch((error) => {
        dispatch(markTempMessageFailed({ tempId, error: error?.message || 'Gửi tin nhắn thất bại' }));
        dispatch(finishSendMessage());
      });
    },
    [dispatch]
  );

  // Handle dismiss failed message
  const handleDismiss = useCallback(
    (failedMessage) => {
      const tempId = failedMessage._id || failedMessage.tempId;
      if (tempId) {
        dispatch(removeTempMessage({ tempId }));
      }
    },
    [dispatch]
  );

  if (!firstGetList) {
    return (
      <Row
        style={{
          padding: 8,
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        justify="center"
      >
        <Spin tip="Đang tải tin nhắn..." />
      </Row>
    );
  }

  // Note: temp messages are now stored directly in messageEntities
  // No need to combine separate optimistic messages anymore

  return (
    <div className="message-list-wrapper">
      <div
        ref={stickyDateRef}
        className={classNames('sticky-date-header', {
          visible: stickyDateVisible && stickyDate,
          merging: isMerging,
        })}
      >
        {stickyDate}
      </div>
      {showScrollDown && (
        <div className="scroll-down-wrapper">
          {newMessagePreview && (
            <div className="new-message-preview" onClick={scrollToBottom}>
              <span className="preview-text">
                {newMessagePreview.senderName}: {newMessagePreview.content}
              </span>
            </div>
          )}
          <Button className="scroll-down-button" icon={<ArrowDownOutlined />} onClick={scrollToBottom} />
        </div>
      )}
      <div
        className="message-list-container"
        id="scrollable-message-list"
        ref={messageListRef}
        onMouseEnter={handleMouseEnter}
        onScroll={handleScroll}
        tabIndex={-1}
      >
        <InfiniteScroll
        scrollThreshold="100px"
        dataLength={messageIds.length}
        next={getMessageList}
        hasMore={hasMore}
        loader={
          <div>
            <MessageSkeleton isOwn={false} />
            <MessageSkeleton isOwn={true} />
            <MessageSkeleton isOwn={false} />
          </div>
        }
        style={{ display: 'flex', flexDirection: 'column-reverse' }}
        inverse
        scrollableTarget="scrollable-message-list"
      >
        {messageIds.map((id, index) => {
          const message = messageEntities[id];
          if (!message) return null;

          const senderId = _get(message, 'sender._id') || _get(message, 'sender');
          const createdAt = _get(message, 'createdAt');
          const currentDateKey = getDateKey(createdAt);

          // Find the next message with a valid createdAt to compare dates
          let nextDateKey = null;
          let foundNextWithDate = false;
          for (let i = index + 1; i < messageIds.length && !foundNextWithDate; i++) {
            const nextMsg = messageEntities[messageIds[i]];
            const nextCreatedAt = _get(nextMsg, 'createdAt');
            if (nextCreatedAt) {
              nextDateKey = getDateKey(nextCreatedAt);
              foundNextWithDate = true;
            }
          }

          // Show date header when:
          // 1. This message has a date AND
          // 2. Either no next message with date exists OR the dates are different
          const showDateHeader = currentDateKey && (!foundNextWithDate || currentDateKey !== nextDateKey);

          const formattedDate = formatDateHeader(createdAt);

          // Determine if avatar should be shown (like Facebook Messenger grouping)
          // Avatar is shown only on the last (bottom) message in a consecutive group
          // from the same sender, where each message is within 5 minutes of the previous
          // Note: Since the list is displayed in reverse (column-reverse), index 0 is the newest message
          // and we need to check the PREVIOUS message (index - 1) which appears BELOW this one
          let showAvatar = true;
          if (index > 0) {
            const prevMessage = messageEntities[messageIds[index - 1]];
            if (prevMessage) {
              const prevSenderId = _get(prevMessage, 'sender._id') || _get(prevMessage, 'sender');
              const prevCreatedAt = _get(prevMessage, 'createdAt');

              // Check if same sender
              if (prevSenderId === senderId) {
                // Check if within 5 minutes (300000 ms)
                const timeDiff = Math.abs(moment(createdAt).diff(moment(prevCreatedAt)));
                if (timeDiff <= 5 * 60 * 1000) {
                  // Same sender and within 5 minutes - hide avatar on this message
                  // (avatar will be shown on the message below, which is prevMessage)
                  showAvatar = false;
                }
              }
            }
          }

          // Determine if sender label should be shown (only on last message in group)
          // In column-reverse: index 0 = newest (bottom), so check index-1 (the message below)
          let showSenderLabel = true;
          if (index > 0) {
            const belowMessage = messageEntities[messageIds[index - 1]];
            if (belowMessage) {
              const belowSenderId = _get(belowMessage, 'sender._id') || _get(belowMessage, 'sender');
              const belowCreatedAt = _get(belowMessage, 'createdAt');
              if (belowSenderId === senderId) {
                const timeDiff = Math.abs(moment(createdAt).diff(moment(belowCreatedAt)));
                if (timeDiff <= 5 * 60 * 1000) {
                  showSenderLabel = false;
                }
              }
            }
          }

          return (
            <div key={id} data-message-date={formattedDate}>
              {showDateHeader && (
                <div className="date-header" data-date-header={formattedDate}>
                  {formattedDate}
                </div>
              )}
              <MessageCell
                isOwnCell={
                  senderId === currentUserId ||
                  (['admin', 'super_admin'].includes(currentUserRole) && _get(message, 'isAdmin'))
                }
                data={message}
                onRetry={handleRetry}
                onDismiss={handleDismiss}
                showAvatar={showAvatar}
                showSenderLabel={showSenderLabel}
              />
            </div>
          );
        })}
      </InfiniteScroll>
      </div>
    </div>
  );
};

MessageList.propTypes = {
  messageIds: PropTypes.array.isRequired,
  messageEntities: PropTypes.object.isRequired,
  getMessageList: PropTypes.func,
  hasMore: PropTypes.bool,
  currentUserId: PropTypes.string,
  currentUserRole: PropTypes.string,
};

export default memo(MessageList);
